import { Dialog, Transition } from '@headlessui/react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { TWButton, TWDataTable } from '../../components/ui';
import { headerCls, EditAction, DataTableSkeleton } from '../../data/columns';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import ModalDelete from '../../components/modal-delete/modal-delete';
import settingsApi from '../../services/settingsApi';
import './templates.css';

const AVATAR_GRADIENTS = [
  'from-sky-500 to-blue-600',
  'from-violet-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-fuchsia-500 to-purple-600',
];

const hashToGradient = (value) => {
  if (!value) return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const truncate = (text, limit = 80) => {
  if (!text) return '';
  return text.length > limit ? `${text.substring(0, limit)}…` : text;
};

const CreditCardFees = () => {
  const [templatesList, setTemplatesList] = useState([]);
  const [deletedTemplate, setDeletedTemplate] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    code: '',
    value: '',
  });
  const [dialogs, setDialogs] = useState({ upsert: false, delete: false });
  const [errors, setErrors] = useState({
    name: false,
    code: false,
    value: false,
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });

  const cancelButtonRef = useRef(null);

  const getTemplatesList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    try {
      const query = `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;
      const list = await settingsApi.list(query);
      if (list.data.data.length > 0) {
        setTemplatesList(list.data.data);
        setPagination((prev) => ({
          ...prev,
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        }));
      } else {
        setTemplatesList([]);
        setPagination((prev) => ({ ...prev, totalRecords: 0 }));
      }
    } catch (error) {
      setTemplatesList([]);
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    getTemplatesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templatesList;
    const needle = search.trim().toLowerCase();
    return templatesList.filter((tpl) => {
      const name = (tpl.name || '').toLowerCase();
      const code = (tpl.code || '').toLowerCase();
      const value = (tpl.value || '').toLowerCase();
      return (
        name.includes(needle) || code.includes(needle) || value.includes(needle)
      );
    });
  }, [templatesList, search]);

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const onClickEdit = (val) => {
    setForm({
      id: val.id,
      name: val.name || '',
      code: val.code || '',
      value: val.value || '',
    });
    setErrors({ name: false, code: false, value: false });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    const nextErrors = {
      name: !form.name,
      code: !form.code,
      value: !form.value,
    };
    if (nextErrors.name || nextErrors.code || nextErrors.value) {
      setErrors(nextErrors);
      return;
    }
    setErrors({ name: false, code: false, value: false });

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      await settingsApi.upsert(form);
    } catch (error) {
      // swallow to match existing behavior
    } finally {
      await getTemplatesList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    if (loading.upsertLoading) return;
    setErrors({ name: false, code: false, value: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (tpl) => {
    setDeletedTemplate(tpl);
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await settingsApi.deleteById(deletedTemplate.id);
    } catch (error) {
      // swallow to match existing behavior
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getTemplatesList();
    }
  };

  const showPagination =
    pagination.totalRecords > pagination.pageSize && !search.trim();

  return (
    <div className="templates-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        {/* Header */}
        <header className="reveal flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
              Templates
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
              Reusable content snippets used throughout the platform.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, code or value"
                className="h-10 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm text-slate-700 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20 sm:w-72"
              />
            </div>
          </div>
        </header>

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={6}
            headers={[
              { label: 'Name', w: 'w-40', shape: 'avatar' },
              { label: 'Code', w: 'w-24' },
              { label: 'Value', w: 'w-64' },
              { align: 'right', w: 'w-8', shape: 'circle' },
            ]}
          />
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-slate-700/60 dark:text-slate-500">
              <DocumentDuplicateIcon className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {search.trim()
                  ? 'No templates match your search.'
                  : 'No templates yet.'}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {search.trim()
                  ? 'Try a different name, code or value.'
                  : 'Templates will appear here once created.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={[
                {
                  title: <span className={headerCls}>Name</span>,
                  key: 'name',
                  render: (_, tpl) => {
                    const gradient = hashToGradient(tpl.code || tpl.name);
                    const initial = (tpl.name || tpl.code || 'T')
                      .charAt(0)
                      .toUpperCase();
                    return (
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-sm font-semibold text-white shadow-sm`}
                        >
                          {initial}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {tpl.name || '—'}
                          </p>
                        </div>
                      </div>
                    );
                  },
                },
                {
                  title: <span className={headerCls}>Code</span>,
                  key: 'code',
                  render: (_, tpl) => (
                    <span className="inline-block max-w-full truncate rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700 dark:bg-slate-700/60 dark:text-slate-200">
                      {tpl.code || '—'}
                    </span>
                  ),
                },
                {
                  title: <span className={headerCls}>Value</span>,
                  key: 'value',
                  render: (_, tpl) => (
                    <p className="truncate text-sm text-slate-600 dark:text-slate-300">
                      {truncate(tpl.value, 100) || '—'}
                    </p>
                  ),
                },
                {
                  title: <span className="sr-only">Actions</span>,
                  key: 'actions',
                  align: 'right',
                  width: 60,
                  render: (_, tpl) => (
                    <EditAction
                      onClick={() => onClickEdit(tpl)}
                      label="Edit template"
                    />
                  ),
                },
              ]}
              data={filteredTemplates}
              rowKey="id"
              pagination={
                showPagination
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} templates`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      {/* Upsert Modal */}
      <Transition.Root show={dialogs.upsert} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={() => null}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full transform overflow-hidden rounded-md bg-white text-left shadow-2xl transition-all dark:bg-slate-800 sm:my-8 sm:max-w-2xl">
                  {/* Modal header */}
                  <div className="flex items-start justify-between border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-sm">
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {form.id ? 'Edit Template' : 'New Template'}
                        </Dialog.Title>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {form.id
                            ? 'Update the template content below.'
                            : 'Create a reusable content snippet.'}
                        </p>
                      </div>
                    </div>
                    <button
                      ref={cancelButtonRef}
                      type="button"
                      onClick={onCloseUpsert}
                      className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-200/60 hover:text-slate-600 dark:hover:bg-slate-700/60 dark:hover:text-slate-200"
                    >
                      <XMarkIcon className="h-5 w-5" />
                      <span className="sr-only">Close</span>
                    </button>
                  </div>

                  {/* Modal body */}
                  <div className="space-y-5 px-6 py-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                      >
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={(e) => onFormChange(e.target.value, 'name')}
                        className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 ${
                          errors.name
                            ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20 dark:border-rose-500/70'
                            : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400/20'
                        }`}
                        placeholder="Template name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                          Name can not be empty
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="code"
                        className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                      >
                        Code
                      </label>
                      <input
                        id="code"
                        name="code"
                        type="text"
                        value={form.code}
                        onChange={(e) => onFormChange(e.target.value, 'code')}
                        className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 font-mono text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 ${
                          errors.code
                            ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20 dark:border-rose-500/70'
                            : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400/20'
                        }`}
                        placeholder="e.g. welcome_email"
                      />
                      {errors.code && (
                        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                          Code can not be empty
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="value"
                        className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                      >
                        Value
                      </label>
                      <textarea
                        id="value"
                        name="value"
                        rows={5}
                        value={form.value}
                        onChange={(e) => onFormChange(e.target.value, 'value')}
                        className={`mt-2 block w-full resize-y rounded-md border bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900/70 dark:text-slate-200 dark:placeholder:text-slate-500 ${
                          errors.value
                            ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20 dark:border-rose-500/70'
                            : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400/20'
                        }`}
                        placeholder="e.g. Served with chef's special sauce and fries"
                      />
                      {errors.value && (
                        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                          Value can not be empty
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Modal footer */}
                  <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                    <div>
                      {form.id && (
                        <TWButton
                          variant="danger"
                          icon={TrashIcon}
                          label="Delete"
                          disabled={loading.deleteLoading}
                          loading={loading.deleteLoading}
                          onClick={() => onClickDelete(form)}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <TWButton
                        variant="default"
                        label="Cancel"
                        disabled={loading.upsertLoading}
                        onClick={onCloseUpsert}
                      />
                      <TWButton
                        variant="primary"
                        icon={!form.id ? PlusIcon : undefined}
                        label={form.id ? 'Save Changes' : 'Create Template'}
                        disabled={loading.upsertLoading}
                        loading={loading.upsertLoading}
                        onClick={onFormSubmit}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <ModalDelete
        title="Delete Template"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedTemplate}
      />
    </div>
  );
};

export default CreditCardFees;
